import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Calendar as CalendarIcon,
  Clock,
  Briefcase,
  Wrench,
  Users,
  X,
  ExternalLink,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';

const eventColors = {
  job: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
  maintenance: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300' },
  meeting: { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300' },
  deadline: { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-300' },
  other: { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300' }
};

const eventIcons = {
  job: Briefcase,
  maintenance: Wrench,
  meeting: Users,
  deadline: Clock,
  other: CalendarIcon
};

export const Calendar = () => {
  const { api } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [filterType, setFilterType] = useState('');
  const [syncUrl, setSyncUrl] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [equipment, setEquipment] = useState([]);

  useEffect(() => {
    fetchData();
  }, [currentDate]);

  const fetchData = async () => {
    try {
      const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
      
      const [eventsRes, jobsRes, equipRes] = await Promise.all([
        api.get('/calendar/events', {
          params: {
            start_date: startOfMonth.toISOString().split('T')[0],
            end_date: endOfMonth.toISOString().split('T')[0]
          }
        }),
        api.get('/jobs'),
        api.get('/inventory')
      ]);
      
      setEvents(eventsRes.data);
      setJobs(jobsRes.data);
      setEquipment(equipRes.data);
    } catch (error) {
      console.error('Error fetching calendar data:', error);
      toast.error('Failed to load calendar');
    } finally {
      setLoading(false);
    }
  };

  const getGoogleSyncUrl = async () => {
    try {
      const response = await api.get('/calendar/sync/google');
      setSyncUrl(response.data);
      toast.success('Sync URL generated');
    } catch (error) {
      toast.error('Failed to generate sync URL');
    }
  };

  const googleCalUrl = (event) => {
    const fmt = (s, allDay) => {
      if (!s) return '';
      const datePart = s.split('T')[0].replace(/-/g, '');
      if (allDay) return datePart;
      const timePart = (s.split('T')[1] || '00:00:00').replace(/[:.]/g, '').slice(0, 6);
      return `${datePart}T${timePart}Z`;
    };
    let start = fmt(event.start_date, event.all_day);
    let end = fmt(event.end_date || event.start_date, event.all_day);
    if (event.all_day) {
      const base = (event.end_date || event.start_date || '').split('T')[0];
      const d = new Date(base + 'T00:00:00');
      d.setDate(d.getDate() + 1);
      end = d.toISOString().split('T')[0].replace(/-/g, '');
    }
    const params = new URLSearchParams({
      text: event.title || 'Event',
      dates: `${start}/${end}`,
      details: event.description || '',
      location: event.job_name || '',
    });
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&${params.toString()}`;
  };

  const createEvent = async (eventData) => {
    try {
      await api.post('/calendar/events', eventData);
      toast.success('Event created');
      fetchData();
      setShowAddModal(false);
    } catch (error) {
      toast.error('Failed to create event');
    }
  };

  // Calendar calculations
  const daysInMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay();
  const monthName = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  const calendarDays = useMemo(() => {
    const days = [];
    // Previous month padding
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push({ day: null, events: [] });
    }
    // Current month days
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayEvents = events.filter(e => {
        const eventDate = e.start_date?.split('T')[0];
        return eventDate === dateStr && (!filterType || e.event_type === filterType);
      });
      days.push({ day, date: dateStr, events: dayEvents });
    }
    return days;
  }, [currentDate, events, filterType, daysInMonth, firstDayOfMonth]);

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const selectedDateEvents = selectedDate ? calendarDays.find(d => d.date === selectedDate)?.events || [] : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#C6A87C] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="calendar-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="font-heading text-3xl">Calendar</h1>
          <p className="text-gray-500 mt-1">Jobs, maintenance, and events</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={getGoogleSyncUrl}
            className="inline-flex items-center gap-2 border border-[#C6A87C] text-[#C6A87C] px-4 py-2 text-xs font-semibold uppercase hover:bg-[#C6A87C]/5"
            data-testid="google-sync-btn"
          >
            <ExternalLink className="w-4 h-4" />
            Google Sync
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="inline-flex items-center gap-2 bg-[#C6A87C] text-white px-6 py-3 text-xs font-semibold uppercase hover:bg-[#B09265]"
            data-testid="add-event-btn"
          >
            <Plus className="w-4 h-4" />
            Add Event
          </button>
        </div>
      </div>

      {/* Sync URL Display */}
      {syncUrl && (
        <div className="bg-green-50 border border-green-200 p-4">
          <p className="font-medium text-green-800 mb-2">Google Calendar Sync URL</p>
          <div className="bg-white p-2 rounded text-sm font-mono text-gray-600 break-all mb-2">
            {syncUrl.ics_url}
          </div>
          <p className="text-sm text-green-700">{syncUrl.instructions}</p>
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-4">
        <Filter className="w-5 h-5 text-gray-400" />
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-4 py-2 border border-gray-200 focus:border-[#C6A87C] focus:outline-none bg-white"
        >
          <option value="">All Events</option>
          <option value="job">Jobs</option>
          <option value="maintenance">Maintenance</option>
          <option value="meeting">Meetings</option>
          <option value="deadline">Deadlines</option>
        </select>
        <div className="flex gap-2 ml-auto">
          {Object.entries(eventColors).map(([type, colors]) => (
            <span key={type} className={`px-2 py-1 text-xs ${colors.bg} ${colors.text} capitalize`}>
              {type}
            </span>
          ))}
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="bg-white border border-gray-200">
        {/* Month Navigation */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <button onClick={prevMonth} className="p-2 hover:bg-gray-100 rounded">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h2 className="font-heading text-xl">{monthName}</h2>
          <button onClick={nextMonth} className="p-2 hover:bg-gray-100 rounded">
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>

        {/* Day Headers */}
        <div className="grid grid-cols-7 border-b border-gray-200">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
            <div key={day} className="p-2 text-center text-xs font-semibold text-gray-500 uppercase">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7">
          {calendarDays.map((dayData, idx) => {
            const isToday = dayData.date === new Date().toISOString().split('T')[0];
            const isSelected = dayData.date === selectedDate;
            
            return (
              <div
                key={idx}
                onClick={() => dayData.day && setSelectedDate(dayData.date)}
                className={`min-h-[100px] p-2 border-b border-r border-gray-100 cursor-pointer transition-colors
                  ${!dayData.day ? 'bg-gray-50' : 'hover:bg-gray-50'}
                  ${isSelected ? 'bg-[#C6A87C]/10 ring-2 ring-[#C6A87C] ring-inset' : ''}
                `}
              >
                {dayData.day && (
                  <>
                    <div className={`text-sm font-medium mb-1 ${isToday ? 'w-7 h-7 bg-[#C6A87C] text-white rounded-full flex items-center justify-center' : ''}`}>
                      {dayData.day}
                    </div>
                    <div className="space-y-1">
                      {dayData.events.slice(0, 3).map((event, eIdx) => {
                        const colors = eventColors[event.event_type] || eventColors.other;
                        return (
                          <div
                            key={eIdx}
                            className={`text-xs p-1 truncate ${colors.bg} ${colors.text} rounded`}
                            title={event.title}
                          >
                            {event.title}
                          </div>
                        );
                      })}
                      {dayData.events.length > 3 && (
                        <div className="text-xs text-gray-500">+{dayData.events.length - 3} more</div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Selected Date Events */}
      {selectedDate && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg">
              {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </h3>
            <button onClick={() => setSelectedDate(null)} className="p-1 hover:bg-gray-100">
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {selectedDateEvents.length > 0 ? (
            <div className="space-y-3">
              {selectedDateEvents.map((event, idx) => {
                const colors = eventColors[event.event_type] || eventColors.other;
                const Icon = eventIcons[event.event_type] || CalendarIcon;
                return (
                  <div key={idx} className={`p-4 border ${colors.border} ${colors.bg}`}>
                    <div className="flex items-start gap-3">
                      <Icon className={`w-5 h-5 ${colors.text} flex-shrink-0 mt-0.5`} />
                      <div className="flex-1">
                        <p className={`font-medium ${colors.text}`}>{event.title}</p>
                        {event.description && (
                          <p className="text-sm text-gray-600 mt-1">{event.description}</p>
                        )}
                        {event.job_name && (
                          <p className="text-sm text-gray-500 mt-1">Job: {event.job_name}</p>
                        )}
                        {event.equipment_name && (
                          <p className="text-sm text-gray-500 mt-1">Equipment: {event.equipment_name}</p>
                        )}
                      </div>
                    </div>
                    <a
                      href={googleCalUrl(event)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-gray-600 hover:text-[#C6A87C] transition-colors"
                      data-testid={`add-to-google-cal-${idx}`}
                    >
                      <CalendarIcon className="w-3.5 h-3.5" />
                      Add to Google Calendar
                    </a>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No events on this day</p>
          )}
        </motion.div>
      )}

      {/* Add Event Modal */}
      <AnimatePresence>
        {showAddModal && (
          <AddEventModal
            jobs={jobs}
            equipment={equipment}
            onClose={() => setShowAddModal(false)}
            onCreate={createEvent}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

// Add Event Modal
const AddEventModal = ({ jobs, equipment, onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    title: '',
    event_type: 'meeting',
    start_date: '',
    end_date: '',
    all_day: true,
    description: '',
    job_id: '',
    equipment_id: ''
  });

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-6 border-b border-gray-100">
          <h2 className="font-heading text-xl">Add Event</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); onCreate(formData); }} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
              placeholder="Event title"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Event Type</label>
            <select
              value={formData.event_type}
              onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
              className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none bg-white"
            >
              <option value="meeting">Meeting</option>
              <option value="deadline">Deadline</option>
              <option value="other">Other</option>
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Start Date *</label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                required
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">End Date</label>
              <input
                type="date"
                value={formData.end_date}
                onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Link to Job</label>
              <select
                value={formData.job_id}
                onChange={(e) => setFormData({ ...formData, job_id: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none bg-white"
              >
                <option value="">None</option>
                {jobs.map(job => (
                  <option key={job.id} value={job.id}>{job.client_name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Link to Equipment</label>
              <select
                value={formData.equipment_id}
                onChange={(e) => setFormData({ ...formData, equipment_id: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none bg-white"
              >
                <option value="">None</option>
                {equipment.map(eq => (
                  <option key={eq.id} value={eq.id}>{eq.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase text-gray-500 mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none resize-none"
              placeholder="Event details..."
            />
          </div>

          <div className="flex justify-end gap-4 pt-4 border-t border-gray-100">
            <button type="button" onClick={onClose} className="px-6 py-3 border border-gray-200 text-gray-600 text-xs font-semibold uppercase">
              Cancel
            </button>
            <button type="submit" className="px-6 py-3 bg-[#C6A87C] text-white text-xs font-semibold uppercase hover:bg-[#B09265]">
              Create Event
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};
